import { startTransition, useActionState, useEffect, useRef, useState } from "react";
import { useEditorCommands, useEditorState } from "@linempo/react-state";
import { errorMessage } from "../../editor-geometry.js";
import { Dialog } from "./Dialog.js";
import { openSample } from "./sample.js";
import { projectStartupFor } from "./project-startup.js";

const RECENT_PROJECT = "linempo.recent-project-id";
type Intent = "save" | "new" | "restore" | "sample";
const readRecent = () => { try { return localStorage.getItem(RECENT_PROJECT) ?? ""; } catch { return ""; } };
export function useProjectActions(importPending: number, onError: (message: string) => void) {
  const state = useEditorState(); const commands = useEditorCommands();
  const current = useRef(state); current.current = state;
  const [recent, setRecent] = useState(readRecent);
  const [confirmation, setConfirmation] = useState<Intent | null>(null);
  const startup = projectStartupFor(commands, recent);
  const [startupState, setStartupState] = useState(startup.getSnapshot);
  useEffect(() => {
    const unsubscribe = startup.subscribe(() => setStartupState(startup.getSnapshot()));
    const detach = startup.attach(() => ({ hasProject: current.current.project !== null, switching: current.current.workspace.switching, disposed: current.current.disposed }));
    setStartupState(startup.getSnapshot());
    return () => { unsubscribe(); detach(); };
  }, [startup, state.workspace.switching]);
  useEffect(() => {
    if (startupState.phase !== "ready" || state.save.status !== "saved" || !state.project || state.savedRevision !== state.project.revision) return;
    try { localStorage.setItem(RECENT_PROJECT, state.project.id); setRecent(state.project.id); }
    catch { /* Saving the project succeeded; a blocked convenience entry is non-fatal. */ }
  }, [startupState.phase, state.save.status, state.savedRevision, state.project?.id, state.project?.revision]);
  const [message, action, pending] = useActionState(async (_previous: string, data: FormData) => {
    const intent = String(data.get("intent")) as Intent;
    const snapshot = current.current;
    if (snapshot.export.locked || snapshot.workspace.switching || importPending > 0) return "请等待当前操作完成。";
    if (intent !== "save" && snapshot.workspace.pending > 0) return "工程正在处理，请稍后再切换。";
    const dirty = snapshot.project && snapshot.project.revision !== snapshot.savedRevision;
    if (intent !== "save" && dirty && !data.has("confirmed")) { setConfirmation(intent); return ""; }
    try {
      if (intent !== "save") startup.supersede();
      commands.clearError();
      if (data.get("confirmed") === "save") await commands.saveProject();
      if (intent === "save") { await commands.saveProject(); return "已保存在此浏览器。"; }
      if (intent === "new") { await commands.newProject("未命名故事"); return "新画纸已展开。"; }
      if (intent === "restore") {
        if (!recent) throw new Error("尚无已保存工程。");
        const restored = await commands.loadProject(recent);
        if (!restored) throw new Error("最近工程已不存在，可能已被浏览器清理。");
        return `已恢复「${restored.name}」。`;
      }
      if (intent === "sample") { await openSample(commands); return "示例已打开，预览将自动更新。"; }
      return "";
    } catch (error) { onError(errorMessage(error)); return ""; }
  }, "");
  const run = (intent: Intent, confirmed?: "save" | "discard") => {
    const data = new FormData(); data.set("intent", intent); if (confirmed) data.set("confirmed", confirmed);
    startTransition(() => action(data));
  };
  const dirty = state.project !== null && state.project.revision !== state.savedRevision;
  useEffect(() => {
    if (!dirty && importPending === 0 && !pending && !state.export.locked && state.workspace.pending === 0) return;
    const prevent = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", prevent); return () => window.removeEventListener("beforeunload", prevent);
  }, [dirty, importPending, pending, state.export.locked, state.workspace.pending]);
  const recovering = startupState.phase === "loading";
  const recoveryDisabled = recovering || state.workspace.switching || pending;
  const recover = (mode: "restore" | "new") => { commands.clearError(); void (mode === "restore" ? startup.retry() : startup.create()); };
  return { recent, message: recovering ? "正在打开工程…" : message, pending: pending || recovering, run, dialog: <>
    {startupState.error && <section role="alert" aria-label="工程恢复失败" className="workspace-dialog" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 40 }}>
      <div className="dialog-heading"><h2>{recent ? "无法恢复最近工程" : "无法创建工程"}</h2></div><p>{startupState.error}</p><p className="help-text">{recent ? "最近工程的记录已保留。可以重试恢复，或新建工程；新工程保存后会成为最近工程。" : "请重试创建工程。"}</p>
      {recovering && <p role="status">正在重试，请稍候…</p>}
      <div className="dialog-actions">{recent && <button type="button" className="button dark" disabled={recoveryDisabled} onClick={() => recover("restore")}>重试恢复</button>}<button type="button" className="button" disabled={recoveryDisabled} onClick={() => recover("new")}>{recent ? "新建工程" : "重试新建"}</button></div>
    </section>}
    {confirmation && <Dialog title="离开当前工程？" onClose={() => setConfirmation(null)}><p>当前工程还有未保存的修改。先保存后继续，或放弃这些修改。</p><div className="dialog-actions"><button type="button" className="button" onClick={() => setConfirmation(null)}>取消</button><button type="button" className="button" onClick={() => { run(confirmation, "discard"); setConfirmation(null); }}>放弃并继续</button><button type="button" className="button dark" onClick={() => { run(confirmation, "save"); setConfirmation(null); }}>保存并继续</button></div></Dialog>}
  </> };
}
